import _ from 'lodash-es';
import { getPrefillIntlPhoneNumber, getSchemaCountryCode } from '../helpers';

/**
 * Normalizes US phone numbers by stripping leading "1" from 11-digit numbers.
 * This prevents the double-prefix bug where backend sends "17074107471" (11 digits)
 * which would become "+1 17074107471 (US)" at submit time.
 *
 * @param {string} phoneNumber - The phone number to normalize
 * @returns {string} - Normalized phone number (10 digits for US numbers)
 */
const normalizeUSPhoneNumber = phoneNumber => {
  if (!phoneNumber) return phoneNumber;
  const digits = phoneNumber.replace(/\D/g, '');
  // If 11 digits starting with "1", strip the leading "1"
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
};

export default function prefillTransformer(pages, formData, metadata, state) {
  // Claimant data from MEB API (primary source)
  const claimant = state.data?.formData?.data?.attributes?.claimant || {};
  const claimantContactInfo = claimant?.contactInfo || {};

  // User profile data (fallback source)
  const {
    dob,
    email: profileEmail,
    ssn: profileSsn,
    userFullName,
    vapContactInfo,
  } = state.user.profile || {};

  const prefillContactInformation = data => {
    const { applicantFullName, email, ssn, mailingAddress, dateOfBirth } = data;

    // Name: prefer formData, then claimant, then profile (my-education-benefits pattern)
    const firstName =
      applicantFullName?.first || claimant?.firstName || userFullName?.first;
    const middleName =
      applicantFullName?.middle || claimant?.middleName || userFullName?.middle;
    const lastName =
      applicantFullName?.last || claimant?.lastName || userFullName?.last;
    const suffix = applicantFullName?.suffix || claimant?.suffix;

    // Only include fields with values
    const prefillName = {};
    if (firstName) prefillName.first = firstName;
    if (middleName) prefillName.middle = middleName;
    if (lastName) prefillName.last = lastName;
    if (suffix) prefillName.suffix = suffix;

    // Email: prefer claimant, fallback to profile
    const emailAddress =
      email ||
      claimantContactInfo.emailAddress ||
      vapContactInfo?.email?.emailAddress ||
      profileEmail ||
      undefined;

    // Phone numbers: prefer claimant, fallback to vapContactInfo
    let mobilePhone;
    if (claimantContactInfo.mobilePhoneNumber) {
      mobilePhone = {
        callingCode: 1,
        countryCode: 'US',
        contact: normalizeUSPhoneNumber(claimantContactInfo.mobilePhoneNumber),
      };
    } else {
      mobilePhone = getPrefillIntlPhoneNumber(vapContactInfo?.mobilePhone);
    }

    let homePhone;
    if (claimantContactInfo.homePhoneNumber) {
      homePhone = {
        callingCode: 1,
        countryCode: 'US',
        contact: normalizeUSPhoneNumber(claimantContactInfo.homePhoneNumber),
      };
    } else {
      homePhone = getPrefillIntlPhoneNumber(vapContactInfo?.homePhone);
    }

    // Date of birth: prefer formData, then claimant, then profile (my-education-benefits pattern)
    const prefillDob = dateOfBirth || claimant?.dateOfBirth || dob;

    // Mailing address priority: Save-in-progress → VAP Profile → Claimant API
    // All fields explicitly set to empty string (not undefined) to prevent address blending
    let prefillMailingAddress;

    // Priority 1: Save-in-progress data (user's edits take precedence)
    // Check if any non-empty mailingAddress object exists, not just street
    if (mailingAddress && Object.keys(mailingAddress).length > 0) {
      prefillMailingAddress = {
        street: mailingAddress.street || '',
        street2: mailingAddress.street2 || '',
        city: mailingAddress.city || '',
        state: mailingAddress.state || '',
        postalCode: mailingAddress.postalCode || '',
        country: mailingAddress.country || 'USA',
        isMilitary: mailingAddress.isMilitary || undefined,
      };
    }
    // Priority 2: VAP Profile (current VA address)
    else if (vapContactInfo?.mailingAddress?.addressLine1) {
      prefillMailingAddress = {
        street: vapContactInfo.mailingAddress.addressLine1,
        street2: vapContactInfo.mailingAddress.addressLine2 || '',
        city: vapContactInfo.mailingAddress.city || '',
        state: vapContactInfo.mailingAddress.stateCode || '',
        postalCode: vapContactInfo.mailingAddress.zipCode || '',
        country: vapContactInfo.mailingAddress.countryCodeIso3 || 'USA',
      };
    }
    // Priority 3: Claimant API (fallback)
    else if (
      claimantContactInfo.addressLine1 &&
      claimantContactInfo.city &&
      claimantContactInfo.countryCode
    ) {
      prefillMailingAddress = {
        street: claimantContactInfo.addressLine1,
        street2: claimantContactInfo.addressLine2 || '',
        city: claimantContactInfo.city,
        state: claimantContactInfo.stateCode || '',
        postalCode:
          claimantContactInfo.zipCode || claimantContactInfo.zipcode || '',
        country: getSchemaCountryCode(claimantContactInfo.countryCode),
      };
    }

    const newData = _.omit(data, ['homePhone', 'mobilePhone', 'email', 'dob']);
    newData.contactInfo = {
      homePhone,
      mobilePhone,
      emailAddress,
    };
    newData.dateOfBirth = prefillDob;
    newData.applicantFullName = prefillName;
    newData.mailingAddress = prefillMailingAddress;
    newData.ssn = ssn || profileSsn;
    newData.claimantId = claimant.claimantId;
    newData.educationLettersNotificationMethod = 'text and email';
    return newData;
  };

  /**
   * Use this function in the prefillTransformer to move all bank account
   * information into `view:originalBankAccount`. This is useful when using the
   * PaymentView component, which will display either `bankAccount` or
   * `view:originalBankAccount`.
   *
   * @param {object} data - All the pre-filled form data
   * @returns {object} - A new pre-filled form data object after transformation.
   */
  const prefillBankInformation = data => {
    return {
      ...data,
      bankAccount: {
        ...state.data?.bankInformation,
        accountNumber:
          data.bankAccount?.accountNumber ||
          state.data?.bankInformation?.accountNumber,
        accountNumberConfirmation:
          data.bankAccount?.accountNumber ||
          state.data?.bankInformation?.accountNumber,
        accountType:
          data.bankAccount?.accountType ||
          state.data?.bankInformation?.accountType,
        routingNumber:
          data.bankAccount?.routingNumber ||
          state.data?.bankInformation?.routingNumber,
        routingNumberConfirmation:
          data.bankAccount?.routingNumber ||
          state.data?.bankInformation?.routingNumber,
      },
    };
  };

  const transformations = [prefillContactInformation, prefillBankInformation];

  const applyTransformations = (data = {}, transformer) => {
    return transformer(data);
  };

  const finalFormData = transformations.reduce(applyTransformations, formData);

  return {
    metadata,
    formData: {
      ...finalFormData,
    },
    pages,
    state,
  };
}
